import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { InlineLoader, PageLoader } from 'cat-ecommerce-alloy';
import {
  USER_TYPE_GUEST,
  PRODUCT_VARIANTS,
  REPLACEMENT_LIST,
  BUNDLE_BEAN,
  ADD_TO_CART_ONCOMPLETE_RENDER
} from '../../../../constants/commonConstants';
import { getProductPurchaseStatus, PRODUCT_STATUS } from './utils';
import AddToCartActions from './AddToCartActions';
import CheckPriceActions from './CheckPriceActions';
import ContactDealerActions from './ContactDealerActions';
import ViewReplacementActions from './ViewReplacementActions';
import { useAddToCart } from '../../../../hooks';
import ProductReplacedParts from './ProductReplacedParts';
import { normalizeError } from '../../../../store/exception/utils';
import { clearError, setError } from '../../../../store/exception/actions';
import { productShape } from '../../../../hooks/useAnalytics/useAnalytics.proptypes';
import { getBundlePrice } from '@app/store/products/actions';
import useAnalytics from '@app/hooks/useAnalytics';
import { useFitmentCheck } from '@app/hooks';
import CustomizeAction from './CustomizeAction';
import CustomCartToast from '../../toasts/AddToCartToast/customCartToast';
import WrapperContainer from '../../WrapperContainer';
import { convertFormattedNumberToUnformatted } from '@app/components/pages/checkout/utils';
import { CatButton } from '@blocks-react/components/Button';

const ProductPrimaryActions = ({
  errorInfo,
  gaListName,
  handleAddToCartError,
  hideCheckPrice,
  itemCatEntryId,
  priceCallStatus,
  productClickDetails,
  productId,
  quantity,
  quantityError,
  setQuantity,
  setQuantityError,
  suppressLoader,
  url = '',
  variant,
  isPackage,
  isPackageWithItemAssociated,
  selectedQuantity,
  partNumber,
  hideOtherInputs = false,
  isDiscontinued = false,
  quantityAvailable
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [derivedPurchaseStatus, setDerivedPurchaseStatus] = useState(
    PRODUCT_STATUS.unknown
  );
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const userType = useSelector(s => s.common?.userType);
  const isInstantAccess = useSelector(s => s.common?.isInstantAccess);
  const registrationPendingApproval = useSelector(
    s => s.common?.registrationPendingApproval
  );
  const { getFitmentCheck } = useFitmentCheck();
  // When used in ProductRelationCard the catEntryId comes as a prop
  const catEntryId =
    useSelector(s => s.products?.byId?.[productId]?.itemCatEntryId) ??
    itemCatEntryId;
  const formattedZeroPrice = useSelector(
    s => s.dealerCustomer?.formattedZeroPrice
  );
  const enableAddToCart = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.enableAddToCart
  );
  const indirectReplacement = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.indirectReplacement
  );
  const sosCode = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.sosCode
  );
  const replacementParts = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.replacementParts
  );
  const unitPrice = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.discountedPrice
  );
  const unformattedTotalPrice = useSelector(
    s => s.products.priceAndAvailability?.[productId]?.unformattedTotalPrice
  );
  const currentID = useSelector(s => s.products.current);
  const beanType = useSelector(s => s.products.byId?.[currentID]?.type);

  const priceAndAvailability = useSelector(
    s => s.products?.priceAndAvailability
  );
  const current = useSelector(s => s.products?.current);
  const productComponents = useSelector(
    s => s.products?.byId?.[current]?.components
  );
  const isCustomizable = useSelector(
    s => s.products.byId?.[current]?.isCustomizable
  );

  const dealerStore = useSelector(s => s.common?.storeLocationName) ?? '';
  const isDiscontinuedCTA = quantityAvailable === 0 && isDiscontinued;

  const isBundle = beanType === BUNDLE_BEAN;
  const isGuest = userType === USER_TYPE_GUEST;
  const showSmallVariant = variant === PRODUCT_VARIANTS.card;
  const isCustomizeKit = isPackageWithItemAssociated && isCustomizable;
  const model = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const isOneToManyReplacement = replacementParts?.length > 1;

  const { addToCart, error, loading, success, setSuccess } = useAddToCart(
    ADD_TO_CART_ONCOMPLETE_RENDER.BLOCKS_TOAST
  );
  const { fireProductClickEvent, fireAddToCartEvent } = useAnalytics();
  let bundleSosCode = '';
  const addPrimaryPartToCart = async (itemsToAdd = []) => {
    handleAddToCartError && handleAddToCartError(undefined);
    const { domain, path } = errorInfo ?? {};
    if (domain && path) {
      dispatch(clearError(domain, path));
    }
    const orderId = partNumber ?? productId;

    const cartItems = itemsToAdd.length
      ? itemsToAdd
      : [
          {
            [!!catEntryId ? 'productId' : 'partNumber']: catEntryId ?? orderId,
            quantity: `${quantity}`
          }
        ];

    const itemSosCode = isBundle ? bundleSosCode : sosCode;
    try {
      await addToCart(
        cartItems,
        {
          x_calculateOrder: itemSosCode ?? ''
        },
        true
      );
    } catch (error) {
      if (domain && path) {
        const err = normalizeError(error);
        dispatch(setError(domain, path, err));
      }
    }
  };
  const addBundleToCart = () => {
    const componentsArray = productComponents?.filter(
      item => item.uniqueId !== current
    );
    const addToBundleItems = [];
    const products = componentsArray?.map((product, index) => {
      const item = priceAndAvailability?.[product.partNumber] ?? product;
      const itemQuantity = product.quantity * quantity;
      if (item.totalPrice !== formattedZeroPrice) {
        addToBundleItems.push({
          partNumber: product.partNumber,
          quantity: String(itemQuantity)
        });
      }
      // grave the sosCode for 1 of the bundle item for pass to the cart service
      if (!bundleSosCode) {
        bundleSosCode = item.sosCode;
      }

      const checkFitment = getFitmentCheck(item?.partNumber);
      return {
        brand: 'CAT',
        businessUnit: 'PCC',
        packageFlag: item?.type === 'PackageBean' ? 'Yes' : 'No',
        relatedModel: model,
        category: item.categoryPath,
        id: item.partNumber,
        name: product?.name,
        price: convertFormattedNumberToUnformatted(item.totalPrice || ''),
        productWeight: item.weight,
        quantity: itemQuantity,
        checkFitment,
        position: index + 1
      };
    });
    if (gaListName) {
      fireProductClickEvent({ list: gaListName, products, dealerStore });
    }
    const totalPrice = unformattedTotalPrice * quantity;
    fireAddToCartEvent({
      products,
      totalPrice: Number(totalPrice?.toFixed(2)),
      dealerStore
    });
    addPrimaryPartToCart(addToBundleItems);
  };
  const addReplacementParts = useCallback(() => {
    handleAddToCartError && handleAddToCartError(undefined);
    addToCart(
      replacementParts.map(part => ({
        productId: part.itemCatEntryId,
        quantity: `${part.quantity}`
      })),
      {
        x_calculateOrder: sosCode ?? ''
      }
    );
  }, [addToCart, handleAddToCartError, replacementParts, sosCode]);

  const getAddToCartButton = ({
    isDisabled,
    isPlural,
    list = '',
    onAddToCart,
    showQuantityInput,
    showSaveToList
  }) => (
    <>
      <AddToCartActions
        showAddtoCartButtonOnly={hideOtherInputs}
        gaListName={list}
        isDisabled={isDisabled}
        isPlural={isPlural}
        onAddToCart={onAddToCart}
        productClickDetails={productClickDetails}
        quantity={quantity}
        setQuantity={setQuantity}
        quantityError={quantityError}
        setQuantityError={setQuantityError}
        showQuantityInput={!hideOtherInputs && showQuantityInput}
        showSaveToList={showSaveToList && !hideOtherInputs}
        showSmallVariant={showSmallVariant}
        isPackage={isPackage}
        isPackageWithItemAssociated={isPackageWithItemAssociated}
        selectedQuantity={selectedQuantity}
        isBundle={isBundle}
      >
        {loading && <PageLoader />}
      </AddToCartActions>
      {success && (
        <WrapperContainer>
          <CustomCartToast
            toastStatus="success"
            toastContent={t('ITEM_ADDED_TO_CART')}
            handleToastClose={() => setSuccess(false)}
          />
        </WrapperContainer>
      )}
    </>
  );

  const getCheckPriceActions = () => (
    <CheckPriceActions showSmallVariant={showSmallVariant} />
  );

  const getReplacementPartsAction = () => {
    return showSmallVariant ? (
      <ViewReplacementActions
        url={url}
        productClickDetails={productClickDetails}
        gaListName={gaListName}
      />
    ) : (
      <ProductReplacedParts replacementParts={replacementParts}>
        {getAddToCartButton({
          isPlural: isOneToManyReplacement,
          list: isOneToManyReplacement ? REPLACEMENT_LIST : '',
          onAddToCart: addReplacementParts,
          showQuantityInput: false,
          showSaveToList: false
        })}
      </ProductReplacedParts>
    );
  };

  const currentPurchaseStatus = getProductPurchaseStatus({
    enableAddToCart,
    formattedZeroPrice,
    indirectReplacement,
    isCatCorp,
    isGuest,
    isInstantAccess,
    priceCallStatus,
    registrationPendingApproval,
    replacementParts,
    unitPrice,
    isDiscontinuedCTA
  });

  const getCustomizeButton = () => {
    return (
      !hideOtherInputs &&
      isPackage &&
      isCustomizeKit && (
        <CustomizeAction
          isBundle={isBundle}
          selectedQuantity={selectedQuantity}
          disabled={
            currentPurchaseStatus === PRODUCT_STATUS.priceCallLoading ||
            !!quantityError
          }
          gaListName={gaListName}
          quantity={quantity}
          isIndirectReplacement={true}
        />
      )
    );
  };

  const getContactDealerButton = (
    showSaveToList,
    isIndirectReplacement = false
  ) => (
    <>
      <ContactDealerActions
        catEntryId={catEntryId}
        isGuest={isGuest}
        showSaveToList={showSaveToList}
        showSmallVariant={showSmallVariant}
        isIndirectReplacement={isIndirectReplacement}
        getCustomizeButton={getCustomizeButton}
        hideDealerButton={isIndirectReplacement && isPackage && isCustomizeKit}
        partNumber={partNumber}
      />
    </>
  );

  useEffect(() => {
    // This is for the case where we've already shown a loader once and don't want to show it again
    const shouldShowPreviousState =
      suppressLoader &&
      currentPurchaseStatus === PRODUCT_STATUS.priceCallLoading;
    if (!shouldShowPreviousState) {
      setDerivedPurchaseStatus(currentPurchaseStatus);
    }
  }, [currentPurchaseStatus, suppressLoader]);

  // build price and availability for bundle item
  // will call the getBundle price when all the items
  // are fully loaded in priceAndAvailability object
  useEffect(() => {
    if (isBundle) {
      const isObjectEmpty = Object.keys(priceAndAvailability).length === 0;
      const priceIsLoaded =
        priceAndAvailability?.[productId]?.priceIsLoaded || false;
      /*
       * First time the bundle renders
       * the purchase will be unavailable because
       * the priceAndAvailability object
       * doesn't have the price for the current item (the Bundle itselft)
       * so system need to call the getBundlePrice
       * for update the current item
       */
      if (
        !isObjectEmpty &&
        !priceIsLoaded &&
        currentPurchaseStatus === PRODUCT_STATUS.purchaseUnavailable
      ) {
        dispatch(getBundlePrice(priceAndAvailability, productId));
      }
    }
  }, [currentPurchaseStatus]);

  useEffect(() => {
    if (error && handleAddToCartError) {
      handleAddToCartError(error);
    }
  }, [error, handleAddToCartError]);

  switch (derivedPurchaseStatus) {
    case PRODUCT_STATUS.checkPrice:
      return hideCheckPrice ? null : getCheckPriceActions();
    case PRODUCT_STATUS.directReplacement:
      return hideOtherInputs && showSmallVariant
        ? getAddToCartButton({
            isPlural: isOneToManyReplacement,
            list: isOneToManyReplacement ? REPLACEMENT_LIST : '',
            onAddToCart: addReplacementParts,
            showQuantityInput: false,
            showSaveToList: false
          })
        : getReplacementPartsAction();
    case PRODUCT_STATUS.indirectReplacement:
      // don't show save to list if part is replaced
      // isIndirectReplacement === true
      return getContactDealerButton(false, true);
    case PRODUCT_STATUS.priceCallLoading:
      return <InlineLoader />;
    case PRODUCT_STATUS.priceCallFailure:
      return getContactDealerButton(true);
    case PRODUCT_STATUS.purchaseAvailable:
      return getAddToCartButton({
        isDisabled: currentPurchaseStatus === PRODUCT_STATUS.priceCallLoading,
        isPlural: false,
        onAddToCart: isBundle ? addBundleToCart : addPrimaryPartToCart,
        list: gaListName,
        showQuantityInput: true,
        showSaveToList: true
      });
    case PRODUCT_STATUS.priceZero:
      // still show the save to list button if the price comes back as $0
      return getContactDealerButton(true);
    case PRODUCT_STATUS.purchaseUnavailable:
      return getContactDealerButton(true);
    case PRODUCT_STATUS.discontinued:
      return <CatButton disabled>{t('DISCONTINUED_CTA')}</CatButton>;
    case PRODUCT_STATUS.unknown:
      return null;
    default:
      throw new Error(
        `Unimplemented product purchase status '${derivedPurchaseStatus}'`
      );
  }
};

ProductPrimaryActions.propTypes = {
  errorInfo: PropTypes.shape({
    domain: PropTypes.string,
    path: PropTypes.string
  }),
  gaListName: PropTypes.string,
  handleAddToCartError: PropTypes.func,
  hideCheckPrice: PropTypes.bool,
  itemCatEntryId: PropTypes.string,
  priceCallStatus: PropTypes.string,
  productClickDetails: PropTypes.oneOfType([
    productShape,
    PropTypes.arrayOf(productShape)
  ]),
  productId: PropTypes.string,
  quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  quantityError: PropTypes.string,
  setQuantity: PropTypes.func,
  setQuantityError: PropTypes.func,
  suppressLoader: PropTypes.bool,
  url: PropTypes.string,
  variant: PropTypes.string,
  isPackage: PropTypes.bool,
  isPackageWithItemAssociated: PropTypes.bool,
  selectedQuantity: PropTypes.number,
  partNumber: PropTypes.string,
  hideOtherInputs: PropTypes.bool,
  isDiscontinued: PropTypes.bool,
  quantityAvailable: PropTypes.number
};

export default ProductPrimaryActions;
